// Carry Roll Demo slice — load/clear pre-built demo datasets (Stage 10 load mechanism).

import type { StateCreator } from 'zustand';
import type { DemoDataset } from '../data/demoDatasets';
import { DEFAULT_DRIVER_REQUEST } from '@carry-roll/shared-logic';
import { useStore } from './index';
import type { SettingsSlice } from './settingsSlice';
import type { InputSlice } from './inputSlice';
import type { ResultSlice } from './resultSlice';
import type { SessionSlice } from './sessionSlice';
import type { CompareSlice } from './compareSlice';

/**
 * Mutual-exclusion type: a demo slice only adds actions. It reads/writes the
 * other slices' state through the combined store type.
 */
type DemoDeps = SettingsSlice & InputSlice & ResultSlice & SessionSlice & CompareSlice;

export interface DemoSlice {
  /** Apply a demo dataset's snapshot to the store and recompute the result. */
  loadDataset: (dataset: DemoDataset) => void;
  /** Reset the store to its empty/default state (clears all demo data). */
  clearDemoData: () => void;
}

export const createDemoSlice: StateCreator<DemoDeps, [], [], DemoSlice> = (set, get) => ({
  loadDataset: (dataset) => {
    const { request, sessions, bag, scenarios } = dataset.state;
    const patch: Partial<DemoDeps> = { request: structuredClone(request) };
    if (sessions) patch.sessions = structuredClone(sessions);
    if (bag) patch.bag = structuredClone(bag);
    if (scenarios) patch.scenarios = structuredClone(scenarios);
    set(patch);
    get().recompute(get().request);
  },
  clearDemoData: () => {
    set({
      request: structuredClone(DEFAULT_DRIVER_REQUEST),
      sessions: [],
      profiles: [],
      bag: [],
      scenarios: [],
      activeSessionId: null,
    });
    get().recompute(get().request);
    useStore.setState({ persistError: null });
  },
});
